import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { Block, DivBlock, GalleryBlock, ImageBlock } from '@/types/blocks';
import { PublicPostGrid, PublicPostCard } from '@/features/blocks/components/PostGrid';
import { PublicMenuBlock } from '@/features/blocks/components/MenuBlockWidget';
import { VideoPlayerBlock } from '@/features/blocks/components/VideoPlayerBlock';
import { PublicNewsletterSignup } from './PublicNewsletterSignup';
import { PublicArtGenerator } from './PublicArtGenerator';
import { PublicPostSearch } from './PublicPostSearch';
import { blockAnchorId } from '@/features/blocks/lib/blockAnchor';
import { getBlockSizeStyle, getEmbedFrameStyle } from '@/features/blocks/lib/blockSize';
import { getDivClassName, getDivStyle } from '@/features/blocks/lib/divStyle';
import { getTextStyle } from '@/features/blocks/lib/textStyle';
import { supabase } from '@/lib/supabase';

const PUBLIC_IMAGE_RADIUS_CLASS: Record<ImageBlock['radius'], string> = {
  none: 'rounded-none',
  sm: 'rounded-xl',
  lg: 'rounded-2xl',
  full: 'rounded-full',
};

const PUBLIC_GALLERY_RADIUS_CLASS: Record<NonNullable<GalleryBlock['radius']>, string> = {
  none: 'rounded-none',
  sm: 'rounded-lg',
  lg: 'rounded-xl',
  full: 'rounded-full',
};

const PUBLIC_GALLERY_GRID_COLUMNS_CLASS: Record<GalleryBlock['columns'], string> = {
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
};

const PUBLIC_GALLERY_MASONRY_COLUMNS_CLASS: Record<GalleryBlock['columns'], string> = {
  2: 'md:columns-2',
  3: 'md:columns-3',
  4: 'md:columns-4',
};

function clampGalleryBatch(value: number): number {
  if (!Number.isFinite(value)) return 12;
  return Math.max(1, Math.min(200, Math.round(value)));
}

function getLinkedVideoEmbedUrl(url: string): string | null {
  try {
    const parsed = new URL(url, window.location.origin);
    const youtubeMatch = parsed.hostname.replace(/^www\./, '').match(/^(?:youtube\.com|youtu\.be)$/);
    if (youtubeMatch) {
      const id = parsed.hostname === 'youtu.be'
        ? parsed.pathname.slice(1)
        : parsed.searchParams.get('v') ?? parsed.pathname.match(/\/(?:embed|shorts|v)\/([^/]+)/)?.[1];
      return id ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0` : null;
    }
    if (parsed.hostname.replace(/^www\./, '') === 'vimeo.com') {
      const id = parsed.pathname.match(/\/(?:video\/)?(\d+)/)?.[1];
      return id ? `https://player.vimeo.com/video/${id}?autoplay=1` : null;
    }
  } catch {
    return null;
  }
  return null;
}

function isLinkedVideoFile(url: string): boolean {
  try {
    return /\.(?:mp4|webm|ogg)(?:$|[?#])/i.test(new URL(url, window.location.origin).pathname);
  } catch {
    return false;
  }
}

/**
 * Imagem com recorte (auto/quadrado/círculo), cantos configuráveis e, quando
 * `lightbox` está ligado, clique para ampliar em uma janela sobre o site.
 */
function PublicImage({ block, className = '', priority = false, fallbackAlt = 'Imagem do conteúdo' }: { block: ImageBlock; className?: string; priority?: boolean; fallbackAlt?: string }) {
  const [open, setOpen] = useState(false);
  const alt = block.alt.trim() || fallbackAlt;

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (!block.src) return null;

  const radiusClass = PUBLIC_IMAGE_RADIUS_CLASS[block.radius];
  const shapeClass =
    block.shape === 'circle'
      ? 'aspect-square rounded-full object-cover'
      : block.shape === 'square'
        ? `aspect-square object-cover ${radiusClass}`
        : `object-cover ${radiusClass}`;

  const img = (
    <img
      src={block.src}
      alt={alt}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : 'auto'}
      decoding="async"
      className={`w-full ${shapeClass} ${className}`}
    />
  );

  const linkedVideoUrl = block.linkUrl ? getLinkedVideoEmbedUrl(block.linkUrl) : null;
  const linkedVideoFile = block.linkUrl && isLinkedVideoFile(block.linkUrl) ? block.linkUrl : null;

  const modal = open && block.linkUrl && block.linkModal ? (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      onClick={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen(false)}
        title="Fechar"
        className="absolute right-4 top-4 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-white/20"
      >
        <X className="h-5 w-5" />
      </button>
      <div className="flex max-h-[90vh] w-full max-w-5xl items-center justify-center overflow-hidden rounded-2xl bg-slate-950 shadow-2xl" onClick={(event) => event.stopPropagation()}>
        {linkedVideoUrl ? (
          <iframe
            src={linkedVideoUrl}
            title={alt}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="aspect-video w-full border-0"
          />
        ) : linkedVideoFile ? (
          <video src={linkedVideoFile} controls autoPlay playsInline className="max-h-[85vh] max-w-full" />
        ) : (
          <iframe
            src={block.linkUrl}
            title={alt}
            className="h-[75vh] min-h-[360px] w-full border-0 bg-white"
          />
        )}
      </div>
    </div>
  ) : null;

  if (block.linkUrl) {
    if (block.linkModal) {
      return (
        <>
          <button type="button" onClick={() => setOpen(true)} className="block w-full cursor-pointer" aria-label={alt}>
            {img}
          </button>
          {modal}
        </>
      );
    }
    const target = block.linkTarget ?? '_self';
    return (
      <a href={block.linkUrl} target={target} rel={target === '_blank' ? 'noreferrer' : undefined} className="block">
        {img}
      </a>
    );
  }

  if (!block.lightbox) return img;

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="block w-full cursor-zoom-in">
        {img}
      </button>
      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-6 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        >
          <button
            onClick={() => setOpen(false)}
            title="Fechar"
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={block.src}
            alt={alt}
            className="max-h-full max-w-full rounded-lg object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}

function PublicGallery({ block, className = '', priority = false }: { block: GalleryBlock; className?: string; priority?: boolean }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const openImage = openIndex === null ? null : block.images[openIndex] ?? null;
  const radiusClass = PUBLIC_GALLERY_RADIUS_CLASS[block.radius ?? 'none'];
  const isMasonry = (block.layout ?? 'grid') === 'masonry';
  const loadingMode = block.loadingMode ?? 'progressive';
  const initialItems = clampGalleryBatch(block.initialItems ?? 12);
  const itemsPerLoad = clampGalleryBatch(block.itemsPerLoad ?? 12);
  const [visibleCount, setVisibleCount] = useState(() => (loadingMode === 'progressive' ? Math.min(initialItems, block.images.length) : block.images.length));
  const visibleImages = useMemo(() => block.images.slice(0, visibleCount), [block.images, visibleCount]);
  const hasMore = visibleCount < block.images.length;
  const imageShapeClass = isMasonry
    ? radiusClass
    : (block.radius ?? 'none') === 'full'
      ? 'aspect-square rounded-full'
      : `aspect-[4/3] ${radiusClass}`;
  const canNavigate = block.images.length > 1;

  const goTo = (nextIndex: number) => {
    if (block.images.length === 0) return;
    setOpenIndex((nextIndex + block.images.length) % block.images.length);
  };

  const showPrevious = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    if (openIndex !== null) goTo(openIndex - 1);
  };

  const showNext = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    if (openIndex !== null) goTo(openIndex + 1);
  };

  useEffect(() => {
    if (openIndex === null) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenIndex(null);
      if (e.key === 'ArrowLeft') goTo(openIndex - 1);
      if (e.key === 'ArrowRight') goTo(openIndex + 1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [openIndex, block.images.length]);

  useEffect(() => {
    setVisibleCount((current) => {
      if (loadingMode !== 'progressive') return block.images.length;
      return Math.min(Math.max(initialItems, current), block.images.length);
    });
  }, [block.images.length, initialItems, loadingMode]);

  useEffect(() => {
    if (loadingMode !== 'progressive' || !hasMore) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setVisibleCount((current) => Math.min(current + itemsPerLoad, block.images.length));
      },
      { rootMargin: '600px 0px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [block.images.length, hasMore, itemsPerLoad, loadingMode]);

  return (
    <>
      <div className={className}>
        {visibleImages.map((img, i) => {
          const image = (
            <figure className={`group relative overflow-hidden bg-slate-100 transition-transform duration-300 hover:-translate-y-0.5 ${imageShapeClass}`}>
              <img
                src={img.src}
                alt={img.alt}
                loading={priority && i === 0 ? 'eager' : 'lazy'}
                fetchPriority={priority && i === 0 ? 'high' : 'auto'}
                decoding="async"
                className={
                  isMasonry
                    ? 'block h-auto w-full transition-transform duration-500 group-hover:scale-[1.03]'
                    : 'h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]'
                }
              />
              {(img.caption || img.alt) && (
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/72 to-transparent px-3 pb-3 pt-10 text-xs font-semibold text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  {img.caption || img.alt}
                </figcaption>
              )}
            </figure>
          );

          if (!(block.lightbox ?? true)) {
            return <div key={i} className={isMasonry ? 'break-inside-avoid' : undefined}>{image}</div>;
          }

          return (
            <button key={i} type="button" onClick={() => setOpenIndex(i)} className={`block w-full cursor-zoom-in text-left ${isMasonry ? 'break-inside-avoid' : ''}`}>
              {image}
            </button>
          );
        })}
      </div>

      {loadingMode === 'progressive' && hasMore && (
        <div ref={sentinelRef} className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={() => setVisibleCount((current) => Math.min(current + itemsPerLoad, block.images.length))}
            className="rounded-full border border-slate-200 bg-white px-5 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
          >
            Carregar mais fotos
          </button>
        </div>
      )}

      {openImage && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/[0.92] p-4 backdrop-blur-sm md:p-6"
          onClick={() => setOpenIndex(null)}
        >
          <div className="absolute left-4 top-4 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-white/85">
            {(openIndex ?? 0) + 1} de {block.images.length}
          </div>
          <button
            onClick={() => setOpenIndex(null)}
            title="Fechar"
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
          >
            <X className="h-5 w-5" />
          </button>

          {canNavigate && (
            <>
              <button
                type="button"
                onClick={showPrevious}
                title="Imagem anterior"
                className="absolute left-3 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-white/20 md:left-6"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                type="button"
                onClick={showNext}
                title="Próxima imagem"
                className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-white/20 md:right-6"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}

          <figure className="flex max-h-full w-full max-w-6xl flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
            <img
              src={openImage.src}
              alt={openImage.alt}
              className="max-h-[74vh] max-w-full rounded-lg object-contain"
            />
            {(openImage.caption || openImage.alt) && (
              <figcaption className="text-center text-sm text-white/85">{openImage.caption || openImage.alt}</figcaption>
            )}

            {canNavigate && (
              <div className="flex max-w-full gap-2 overflow-x-auto px-2 pb-1">
                {block.images.map((img, index) => (
                  <button
                    key={`${img.src}-${index}`}
                    type="button"
                    onClick={() => setOpenIndex(index)}
                    title={`Abrir imagem ${index + 1}`}
                    className={`h-14 w-20 shrink-0 overflow-hidden rounded-md border transition-opacity ${
                      index === openIndex ? 'border-white opacity-100' : 'border-white/20 opacity-55 hover:opacity-85'
                    }`}
                  >
                    <img src={img.src} alt="" className="h-full w-full bg-white/10 object-contain" />
                  </button>
                ))}
              </div>
            )}
          </figure>
        </div>
      )}
    </>
  );
}

/**
 * Renderer premium para o site público — distinto do BlockRenderer do admin
 * (que é preview de edição). Aqui cada bloco vira uma seção com ritmo vertical
 * generoso, tratamento full-bleed para heros/CTA (blocos html) e cards para
 * colunas. Cores via tokens do tenant (--color-primary / --color-accent).
 */

// Cyan puro é ilegível sobre branco; escurece para uso como texto/detalhe claro.
const ACCENT_ON_LIGHT = 'color-mix(in oklab, var(--color-accent), #0b1220 42%)';

// Cor/fonte de título e parágrafo vêm da aparência resolvida (site ou modelo),
// aplicada como CSS var no wrapper (ver appearanceCssVars) — fallback aqui
// cobre o caso de o wrapper não ter definido a var (ex.: uso fora do site público).
const HEADING_STYLE: React.CSSProperties = { color: 'var(--color-heading, #0f172a)', fontFamily: 'var(--font-heading, var(--font-display))' };
const PARAGRAPH_STYLE: React.CSSProperties = { color: 'var(--color-paragraph, #475569)', fontFamily: 'var(--font-paragraph, var(--font-body))' };

/**
 * Tamanho vem do campo único de Aparência (heading/body), escalado por
 * contexto (citação > título de seção > subtítulo de card) para preservar
 * alguma hierarquia visual mesmo com uma única configuração de base.
 */
const headingStyle = (scale = 1): React.CSSProperties => ({ ...HEADING_STYLE, fontSize: `calc(var(--font-size-heading, 36px) * ${scale})` });
const paragraphStyle = (scale = 1): React.CSSProperties => ({ ...PARAGRAPH_STYLE, fontSize: `calc(var(--font-size-paragraph, 18px) * ${scale})` });

interface Props {
  blocks: Block[];
  /** Contexto do site para blocos dinâmicos (ex.: grid de posts). */
  siteId?: string;
  siteSlug?: string;
  templateDepth?: number;
  /** Usado quando uma imagem legada não recebeu texto alternativo no editor. */
  fallbackImageAlt?: string;
}

export function PublicBlockRenderer({ blocks, siteId, siteSlug, templateDepth = 0, fallbackImageAlt }: Props) {
  return (
    <>
      {blocks.map((block, index) => (
        <BlockSizeFrame key={block.id} block={block}>
          <PublicSection block={block} siteId={siteId} siteSlug={siteSlug} templateDepth={templateDepth} priority={index === 0} fallbackImageAlt={fallbackImageAlt} />
        </BlockSizeFrame>
      ))}
    </>
  );
}

function BlockSizeFrame({ block, children }: { block: Block; children: React.ReactNode }) {
  return (
    <div
      id={blockAnchorId(block)}
      className="min-w-0"
      style={{ ...getBlockSizeStyle(block, true), scrollMarginTop: 'var(--public-anchor-offset, 96px)' }}
    >
      {children}
    </div>
  );
}

function PublicSection({ block, siteId, siteSlug, templateDepth, priority = false, fallbackImageAlt }: { block: Block; siteId?: string; siteSlug?: string; templateDepth: number; priority?: boolean; fallbackImageAlt?: string }) {
  if (block.type === 'template') {
    return <PublicTemplateBlock block={block} siteId={siteId} siteSlug={siteSlug} templateDepth={templateDepth} />;
  }

  // Blocos html são full-bleed: trazem o próprio visual (hero, faixas, CTA).
  if (block.type === 'html') {
    return <PublicHtmlBlock html={block.html} />;
  }

  if (block.type === 'postgrid') {
    if (!siteId || !siteSlug) return null;
    return <PublicPostGrid block={block} siteId={siteId} siteSlug={siteSlug} />;
  }

  if (block.type === 'post_card') {
    if (!siteId || !siteSlug) return null;
    return <PublicPostCard block={block} siteId={siteId} siteSlug={siteSlug} />;
  }

  if (block.type === 'menu') {
    if (!siteId || !siteSlug) return null;
    return <PublicMenuBlock block={block} siteId={siteId} siteSlug={siteSlug} />;
  }

  if (block.type === 'art_generator') {
    return <PublicArtGenerator block={block} siteId={siteId} />;
  }
  if (block.type === 'post_search') {
    if (!siteId || !siteSlug) return null;
    return <PublicPostSearch block={block} siteId={siteId} siteSlug={siteSlug} />;
  }

  if (block.type === 'div') {
    // Sem max-width aqui: "largura total" vs. "contida" é decidido pela
    // própria div (getDivClassName) — um wrapper com max-w ou padding lateral
    // fixo anularia o modo largura total (que deve encostar nas bordas).
    // O padding de segurança só entra quando a div é "contida".
    //
    // Sem py-6: esse padding vertical era fixo e invisível no editor (o
    // canvas admin renderiza a div sem esse wrapper) — ficava um espaço que
    // ninguém tinha como remover pelo painel. Espaçamento vertical agora é
    // só o que a própria div/bloco configurar (margem externa/padding
    // interno), igual ao que se vê no editor.
    return (
      <section className={`w-full ${block.contentWidth === 'boxed' ? 'px-6' : ''}`}>
        <PublicDiv block={block} siteId={siteId} siteSlug={siteSlug} templateDepth={templateDepth} />
      </section>
    );
  }

  if (block.type === 'columns') {
    const cols = block.columns.length || 1;
    const gridClass =
      cols >= 4 ? 'md:grid-cols-4' : cols === 3 ? 'md:grid-cols-3' : cols === 2 ? 'md:grid-cols-2' : 'md:grid-cols-1';
    return (
      <section className="w-full bg-slate-50 py-20 md:py-28">
        <div className={`mx-auto max-w-6xl px-6 grid grid-cols-1 ${gridClass} gap-6`}>
          {block.columns.map((column, ci) => (
            <div
              key={ci}
              className="group animate-fade-in rounded-2xl border border-slate-200/80 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:border-transparent"
              style={{ animationDelay: `${ci * 90}ms` }}
            >
              <div className="mb-6 h-1.5 w-12 rounded-full" style={{ background: 'var(--color-accent)' }} />
              {column.map((nested) => (
                <div key={nested.id}>{renderCardBlock(nested, siteId, siteSlug, templateDepth)}</div>
              ))}
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (block.type === 'quote') {
    return (
      <section className="w-full py-24 md:py-32">
        <figure className="mx-auto max-w-3xl px-6 text-center">
          <div
            aria-hidden
            className="mx-auto mb-6 font-display text-7xl leading-none"
            style={{ color: ACCENT_ON_LIGHT, opacity: 0.35 }}
          >
            &ldquo;
          </div>
          <blockquote className="font-semibold leading-snug tracking-tight" style={headingStyle(1.2)}>
            {block.content}
          </blockquote>
          {block.author && (
            <figcaption className="mt-6 text-sm font-medium uppercase tracking-widest" style={{ color: ACCENT_ON_LIGHT }}>
              {block.author}
            </figcaption>
          )}
        </figure>
      </section>
    );
  }

  if (block.type === 'heading') {
    const Tag = `h${block.level}` as keyof React.JSX.IntrinsicElements;
    const isSection = block.level <= 2;
    return (
      <section className={`w-full ${isSection ? 'pt-24 md:pt-32 pb-4 text-center' : 'pt-12 pb-2'}`}>
        <div className="mx-auto max-w-3xl px-6">
          {isSection && (
            <div className="mx-auto mb-5 h-1 w-14 rounded-full" style={{ background: 'var(--color-accent)' }} />
          )}
          {React.createElement(
            Tag,
            {
              className: isSection ? 'font-extrabold tracking-tight' : 'font-bold',
              style: { ...headingStyle(isSection ? 1 : 0.65), ...getTextStyle(block) },
            },
            block.content,
          )}
        </div>
      </section>
    );
  }

  if (block.type === 'paragraph') {
    return (
      <section className="w-full pb-2 pt-4">
        <p className="mx-auto max-w-2xl px-6 text-center leading-relaxed" style={{ ...paragraphStyle(1.1), ...getTextStyle(block) }}>
          {block.content}
        </p>
      </section>
    );
  }

  if (block.type === 'button') {
    const align = block.align === 'center' ? 'justify-center' : block.align === 'right' ? 'justify-end' : 'justify-start';
    return (
      <section className="w-full py-8">
        <div className={`mx-auto flex max-w-3xl px-6 ${align}`}>
          <PublicButton label={block.label} url={block.url} variant={block.variant} textStyle={getTextStyle(block)} />
        </div>
      </section>
    );
  }

  if (block.type === 'image') {
    if (!block.src) return null;
    return (
      <section className="w-full py-10">
        <figure className="mx-auto max-w-5xl px-6">
          <PublicImage block={block} priority={priority} fallbackAlt={fallbackImageAlt} className="shadow-2xl ring-1 ring-slate-900/5" />
          {block.caption && <figcaption className="mt-3 text-center text-sm text-slate-500">{block.caption}</figcaption>}
        </figure>
      </section>
    );
  }

  if (block.type === 'gallery') {
    const isMasonry = (block.layout ?? 'grid') === 'masonry';
    const layoutClass = isMasonry
      ? `columns-2 ${PUBLIC_GALLERY_MASONRY_COLUMNS_CLASS[block.columns]} gap-0`
      : `grid grid-cols-2 ${PUBLIC_GALLERY_GRID_COLUMNS_CLASS[block.columns]} gap-0`;
    return (
      <section className="w-full py-10">
        <PublicGallery block={block} priority={priority} className={`mx-auto max-w-6xl px-6 ${layoutClass}`} />
      </section>
    );
  }

  if (block.type === 'video') {
    return (
      <section className="w-full py-10">
        <div className="mx-auto max-w-4xl px-6">
          <VideoPlayerBlock block={block} className="aspect-video w-full rounded-none shadow-2xl" />
        </div>
      </section>
    );
  }

  if (block.type === 'code') {
    return (
      <section className="w-full py-8">
        <pre className="mx-auto max-w-4xl overflow-x-auto rounded-2xl bg-slate-900 p-6 px-6 text-sm text-slate-100 shadow-xl">
          <code className="font-mono">{block.code}</code>
        </pre>
      </section>
    );
  }

  if (block.type === 'embed') {
    if (!block.url) return null;
    return (
      <section className="w-full py-10">
        <div className="mx-auto max-w-4xl px-6">
          <iframe
            src={block.url}
            title={block.caption || 'Conteúdo incorporado'}
            loading="lazy"
            allowFullScreen
            className="w-full rounded-2xl shadow-2xl"
            style={getEmbedFrameStyle(block)}
          />
          {block.caption && <p className="mt-3 text-center text-sm text-slate-500">{block.caption}</p>}
        </div>
      </section>
    );
  }

  if (block.type === 'spacer') {
    // Sem padding de seção próprio — o controle de altura é o objetivo do bloco.
    return <div style={{ height: block.height }} aria-hidden />;
  }

  if (block.type === 'newsletter_signup') {
    return (
      <section className="w-full py-10">
        <div className="px-6">
          <PublicNewsletterSignup block={block} siteId={siteId} />
        </div>
      </section>
    );
  }

  return null;
}

/**
 * Scripts inseridos via dangerouslySetInnerHTML ficam inertes por regra do
 * navegador. No site público, executamos apenas os scripts que pertencem ao
 * próprio bloco HTML, mantendo o preview administrativo sem execução.
 */
function PublicHtmlBlock({ html }: { html: string }) {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const openContactModalFromFloat = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const link = target.closest('a.tp-whatsapp-float');
      const modal = document.getElementById('tp-modal-contato');
      if (!link || !modal || !root.contains(modal)) return;

      event.preventDefault();
      event.stopPropagation();
      modal.classList.add('is-active');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    };
    document.addEventListener('click', openContactModalFromFloat, true);

    // Ícones flutuantes do bloco podem manter o href do WhatsApp como
    // fallback, mas, quando o mesmo bloco contém o modal de contato, passam
    // a acioná-lo pelo seletor já usado pelo script personalizado.
    if (root.querySelector('#tp-modal-contato')) {
      root.querySelectorAll<HTMLAnchorElement>('a.tp-whatsapp-float').forEach((link) => {
        link.setAttribute('data-open-modal', 'contato');
      });
    }

    const inertScripts = Array.from(root.querySelectorAll('script'));
    inertScripts.forEach((source) => {
      const executable = document.createElement('script');
      Array.from(source.attributes).forEach((attribute) => executable.setAttribute(attribute.name, attribute.value));
      executable.textContent = source.textContent;
      source.replaceWith(executable);
    });

    return () => {
      document.removeEventListener('click', openContactModalFromFloat, true);
    };
  }, [html]);

  return <div ref={rootRef} className="w-full" dangerouslySetInnerHTML={{ __html: html }} />;
}

/** Container flexível — renderiza os filhos de forma compacta (sem seção própria por filho), permitindo div dentro de div. */
function PublicDiv({ block, siteId, siteSlug, templateDepth }: { block: DivBlock; siteId?: string; siteSlug?: string; templateDepth: number }) {
  return (
    <div className={getDivClassName(block)} style={getDivStyle(block)}>
      {block.children.map((child) => (
        <React.Fragment key={child.id}>{renderDivChild(child, siteId, siteSlug, templateDepth)}</React.Fragment>
      ))}
    </div>
  );
}

/** Blocos dentro de uma div — compactos, sem o espaçamento generoso de `PublicSection`. */
function renderDivChild(block: Block, siteId?: string, siteSlug?: string, templateDepth = 0) {
  return <BlockSizeFrame block={block}>{renderDivChildContent(block, siteId, siteSlug, templateDepth)}</BlockSizeFrame>;
}

function renderDivChildContent(block: Block, siteId?: string, siteSlug?: string, templateDepth = 0) {
  if (block.type === 'template') {
    return <PublicTemplateBlock block={block} siteId={siteId} siteSlug={siteSlug} templateDepth={templateDepth} compact />;
  }
  if (block.type === 'div') {
    return <PublicDiv block={block} siteId={siteId} siteSlug={siteSlug} templateDepth={templateDepth} />;
  }
  if (block.type === 'menu') {
    return siteId && siteSlug ? <PublicMenuBlock block={block} siteId={siteId} siteSlug={siteSlug} compact /> : null;
  }
  if (block.type === 'image') {
    // Sem o `mb-4` do card de coluna: dentro de uma div flex essa margem
    // desalinha o item em relação ao `items-center` do container (logo do header).
    return block.src ? <PublicImage block={block} /> : null;
  }
  if (block.type === 'postgrid') {
    return siteId && siteSlug ? <PublicPostGrid block={block} siteId={siteId} siteSlug={siteSlug} /> : null;
  }
  if (block.type === 'post_card') {
    return siteId && siteSlug ? <PublicPostCard block={block} siteId={siteId} siteSlug={siteSlug} /> : null;
  }
  if (block.type === 'art_generator') {
    return <PublicArtGenerator block={block} siteId={siteId} />;
  }
  if (block.type === 'post_search') {
    return siteId && siteSlug ? <PublicPostSearch block={block} siteId={siteId} siteSlug={siteSlug} /> : null;
  }
  if (block.type === 'columns') {
    const cols = block.columns.length || 1;
    const gridClass = cols >= 4 ? 'md:grid-cols-4' : cols === 3 ? 'md:grid-cols-3' : cols === 2 ? 'md:grid-cols-2' : 'md:grid-cols-1';
    return (
      <div className={`grid w-full grid-cols-1 ${gridClass} gap-6`}>
        {block.columns.map((column, ci) => (
          <div key={ci} className="space-y-3">
            {column.map((nested) => (
              <div key={nested.id}>{renderCardBlock(nested, siteId, siteSlug, templateDepth)}</div>
            ))}
          </div>
        ))}
      </div>
    );
  }
  if (block.type === 'quote') {
    return (
      <blockquote className="border-l-4 pl-4 italic" style={{ borderColor: 'var(--color-accent)' }}>
        <p style={headingStyle(0.9)}>{block.content}</p>
        {block.author && (
          <cite className="mt-1 block text-sm not-italic" style={paragraphStyle(0.85)}>
            — {block.author}
          </cite>
        )}
      </blockquote>
    );
  }
  if (block.type === 'gallery') {
    const isMasonry = (block.layout ?? 'grid') === 'masonry';
    const layoutClass = isMasonry
      ? `columns-2 ${PUBLIC_GALLERY_MASONRY_COLUMNS_CLASS[block.columns]} gap-0`
      : `grid grid-cols-2 ${PUBLIC_GALLERY_GRID_COLUMNS_CLASS[block.columns]} gap-0`;
    return <PublicGallery block={block} className={`w-full ${layoutClass}`} />;
  }
  if (block.type === 'video') {
    return <VideoPlayerBlock block={block} className="aspect-video w-full rounded-none" />;
  }
  if (block.type === 'code') {
    return (
      <pre className="w-full overflow-x-auto rounded-xl bg-slate-900 p-4 text-sm text-slate-100">
        <code className="font-mono">{block.code}</code>
      </pre>
    );
  }
  if (block.type === 'embed') {
    return block.url ? (
      <iframe
        src={block.url}
        title={block.caption || 'Conteúdo incorporado'}
        loading="lazy"
        allowFullScreen
        className="w-full rounded-xl"
        style={getEmbedFrameStyle(block)}
      />
    ) : null;
  }
  if (block.type === 'html') {
    return <div dangerouslySetInnerHTML={{ __html: block.html }} />;
  }
  if (block.type === 'newsletter_signup') {
    return <PublicNewsletterSignup block={block} siteId={siteId} className="mx-auto" />;
  }
  // heading, paragraph, button, image, spacer — mesmo tratamento compacto do card de coluna.
  return renderCardBlock(block);
}

/** Blocos dentro de um card de coluna — sem seção própria, tipografia compacta. */
function renderCardBlock(block: Block, siteId?: string, siteSlug?: string, templateDepth = 0) {
  return <BlockSizeFrame block={block}>{renderCardBlockContent(block, siteId, siteSlug, templateDepth)}</BlockSizeFrame>;
}

function renderCardBlockContent(block: Block, siteId?: string, siteSlug?: string, templateDepth = 0) {
  if (block.type === 'template') {
    return <PublicTemplateBlock block={block} siteId={siteId} siteSlug={siteSlug} templateDepth={templateDepth} compact />;
  }
  if (block.type === 'heading') {
    return <h3 className="mb-3 font-bold" style={{ ...headingStyle(0.65), ...getTextStyle(block) }}>{block.content}</h3>;
  }
  if (block.type === 'paragraph') {
    return <p className="leading-relaxed" style={{ ...paragraphStyle(), ...getTextStyle(block) }}>{block.content}</p>;
  }
  if (block.type === 'button') {
    return (
      <div className="mt-5">
        <PublicButton label={block.label} url={block.url} variant={block.variant} textStyle={getTextStyle(block)} />
      </div>
    );
  }
  if (block.type === 'image') {
    if (!block.src) return null;
    return <div className="mb-4"><PublicImage block={block} /></div>;
  }
  if (block.type === 'art_generator') {
    return <PublicArtGenerator block={block} siteId={siteId} />;
  }
  if (block.type === 'post_search') {
    return siteId && siteSlug ? <PublicPostSearch block={block} siteId={siteId} siteSlug={siteSlug} /> : null;
  }
  if (block.type === 'spacer') {
    return <div style={{ height: block.height }} aria-hidden />;
  }
  return null;
}

function isBlock(value: unknown): value is Block {
  return typeof value === 'object' && value !== null && 'id' in value && 'type' in value;
}

function getTemplateContentBlocks(content: unknown): Block[] {
  return Array.isArray(content) ? content.filter(isBlock) : [];
}

function PublicTemplateBlock({
  block,
  siteId,
  siteSlug,
  templateDepth,
  compact = false,
}: {
  block: Extract<Block, { type: 'template' }>;
  siteId?: string;
  siteSlug?: string;
  templateDepth: number;
  compact?: boolean;
}) {
  const { data: template } = useQuery({
    queryKey: ['public-appearance-template-block', block.templateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('appearance_templates')
        .select('id, name, content, updated_at')
        .eq('id', block.templateId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: Boolean(block.templateId),
  });

  if (!block.templateId || templateDepth >= 3) return null;

  const blocks = getTemplateContentBlocks(template?.content);
  if (blocks.length === 0) return null;

  if (compact) {
    return (
      <div className="w-full">
        <PublicBlockRenderer blocks={blocks} siteId={siteId} siteSlug={siteSlug} templateDepth={templateDepth + 1} />
      </div>
    );
  }

  return <PublicBlockRenderer blocks={blocks} siteId={siteId} siteSlug={siteSlug} templateDepth={templateDepth + 1} />;
}

function PublicButton({
  label,
  url,
  variant,
  textStyle,
}: {
  label: string;
  url: string;
  variant: 'primary' | 'secondary' | 'outline';
  textStyle?: React.CSSProperties;
}) {
  if (variant === 'outline') {
    return (
      <a
        href={url}
        className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold transition-all hover:-translate-y-0.5"
        style={{ color: ACCENT_ON_LIGHT, border: '1.5px solid color-mix(in oklab, var(--color-accent), transparent 55%)', ...textStyle }}
      >
        {label}
      </a>
    );
  }
  // primary / secondary → pílula navy com glow cyan
  return (
    <a
      href={url}
      className="inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5"
      style={{
        color: '#fff',
        background: 'linear-gradient(135deg, var(--color-primary), color-mix(in oklab, var(--color-primary), #000 18%))',
        boxShadow: '0 10px 30px -8px color-mix(in oklab, var(--color-accent), transparent 55%), 0 0 0 1px color-mix(in oklab, var(--color-accent), transparent 70%)',
        ...textStyle,
      }}
    >
      {label}
    </a>
  );
}
